import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, makeStyles, MessageBar, MessageBarBody, Popover, PopoverSurface, PopoverTrigger, tokens } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import useAxios from '../../../../hooks/useAxios'
import Loading from '../../../../components/Loading'
import { SortDirection } from '../../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    useOverviewStyles,
} from '../../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterType, generateFromDate, SearchHintIcon } from '../../../../pages/BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../../../pages/BaseOverview/BaseSortMenu'
import { Message } from '@microsoft/microsoft-graph-types'
import { EntityDto } from '../../../../config/appConfig'
import FileEmailListItem from './FileEmailListItem'
import { ViewFileProps } from '../ViewFile'
import { Destination, IGraphArrayPayload } from '../../../../context/AxiosContext'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../../../../helpers/referenceNumberHelper'
import BaseHeaderItem from '../../../../pages/BaseOverview/BaseHeaderItem'
import useUser from '../../../../hooks/useUser'
import HammockDialog, { useDialogStyles } from '../../../components/Dialog/HammockDialog'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import PreviewMessageDialogBody, { PreviewMessageDialogBodyProps } from '../../../components/Dialog/Message/PreviewMessageDialogBody'
import FileEmailHeaderActionsButtons, { EmailHeaderActionsOverviewProps } from './FileEmailHeaderActionsButtons'
import FileEmailHeaderFilterTags, { EmailHeaderFilterTagsOverviewProps } from './FileEmailHeaderFilterTags'
import { useTranslation } from 'react-i18next'
import DateRange from '../../../../components/DatePicker/DateRange'

export const useItemStyles = makeStyles({
    listEmailItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewIconWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailItemSelected: {
        backgroundColor: `${tokens.colorBrandBackground2Pressed}!important`,
    },
    listEmailDetails: {
        display: 'grid',
        gridTemplateColumns: '1fr`',
        gridTemplateRows: '1fr 1fr 1fr`',
    },
})

export type ExtendedEmailOverviewDto = Omit<Message, 'id'> &
    EntityDto & {
        inbound?: boolean
        references?: string[]
        url?: string //Custom URL with which the item can be downloaded.
    }

export type EmailOverviewProps = BaseOverviewProps<
    ExtendedEmailOverviewDto,
    EmailFilterType,
    EmailSortingType,
    BaseOverviewCount,
    EmailHeaderActionsOverviewProps,
    EmailHeaderFilterTagsOverviewProps
> &
    ViewFileProps

export type EmailLineOverviewProps = EmailOverviewProps &
    BaseOverviewLineItemProps<ExtendedEmailOverviewDto, EmailFilterType, EmailSortingType> & {
        onPreviewClick: (message: ExtendedEmailOverviewDto) => void
    }

export type EmailFilterType = BaseOverviewFilterType & {
    from?: string
    to?: string
}

export type EmailSorting = {
    sentDateTime: string
}

export type EmailSortingType = BaseOverviewSortingType<EmailSorting>

function EmailFilter() {
    const { values, setFieldValue } = useFormikContext<EmailFilterType>()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.OutlookNewWide) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
        </>
    )
}

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(_e, data) => setOpen(data.open)} withArrow>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.pivot.email.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.pivot.email.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

const headerSortingItems: EmailSortingType[] = [
    { property: ['sentDateTime'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc' },
    { property: ['sentDateTime'], direction: SortDirection.Descending, default: true, descriptionKey: 'sorting.datedesc' },
]

//Graph does not allow $skip/$filter/$orderby in combination with $search, so search results are filtered, sorted and paged client-side.
const SEARCH_MAX_RESULTS = 500

function applySearchFilters(
    items: ExtendedEmailOverviewDto[],
    filterValues: EmailFilterType,
    sortingValues?: EmailSortingType,
): ExtendedEmailOverviewDto[] {
    //Plain string comparison mirrors the midnight-UTC semantics of the browse-mode Graph $filter on sentDateTime.
    const filtered = items.filter(
        item => (!filterValues.from || item.sentDateTime >= filterValues.from) && (!filterValues.to || item.sentDateTime <= filterValues.to),
    )
    if (!sortingValues) {
        return filtered
    }
    return filtered.sort(
        (a, b) => (sortingValues.direction === SortDirection.Ascending ? 1 : -1) * (a.sentDateTime ?? '').localeCompare(b.sentDateTime ?? ''),
    )
}

export default function FileEmailOverview(props: Partial<EmailOverviewProps> & ViewFileProps) {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const dialogStyles = useDialogStyles()
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { currentMailbox } = useUser()
    const { t: translate } = useTranslation()

    const overviewApiRef = useRef<{
        onUpdatePageItems: (column: number, page: number, start: number, size: number) => Promise<ExtendedEmailOverviewDto[]>
    }>()
    const [reloadTrigger, setReloadTrigger] = useState<number>(0)
    const searchCacheRef = useRef<{ key: string; promise: Promise<ExtendedEmailOverviewDto[]> }>()
    const [searchTruncated, setSearchTruncated] = useState<boolean>(false)
    const reload = () => {
        searchCacheRef.current = undefined
        setReloadTrigger(reloadTrigger + 1)
    }

    const [emailReplicationFolderId, setEmailReplicationFolderId] = useState<string | null>()
    const [emailUserFolderId, setEmailUserFolderId] = useState<string>()

    const [count, setCount] = useState<BaseOverviewCount>({ total: 0 })
    const [items, setItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [selectedItems, setSelectedItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [openPreviewMessage, setOpenPreviewMessage] = useState<ExtendedEmailOverviewDto>()

    useEffect(() => {
        axios
            .get('/email/replication/parent-folder/id', { params: { references: [props.file.reference] } })
            .then((response: AxiosResponse<{ [key: string]: string }>) => setEmailReplicationFolderId(response.data[props.file.reference] ?? null))
    }, [props.file])

    useEffect(() => {
        axios
            .get(`/email/${currentMailbox}/folder/${props.file.reference}`, {
                baseURL: Destination.Hammock,
                headers: { SuppressErrors: 'true' },
            })
            // 204 (no folder linked) resolves with an empty body; normalize to undefined.
            .then((response: AxiosResponse<string>) => setEmailUserFolderId(response.data || undefined))
    }, [props.file])

    const defaultFilterValues = useMemo<EmailFilterType>(
        () => ({
            from: '',
            to: '',
            query: '',
            tag: 'all',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const mapMessage = useCallback(
        (item: Message): ExtendedEmailOverviewDto => ({
            id: item.id,
            ...item,
            references: extractReferenceNumbersFromMessage(item),
            inbound:
                item.sender?.emailAddress.address
                    .substring(item.sender?.emailAddress.address.indexOf('@'))
                    .localeCompare(currentMailbox?.substring(currentMailbox?.indexOf('@'))) !== 0,
            url: `${process.env.INBOXER_HOST}/api/download-message?hostKey=${currentHost?.RowKey}&email=${encodeURIComponent(currentHost.ReplicationMail)}&messageId=${item.id}`,
        }),
        [currentMailbox, currentHost],
    )

    const fetchSearchResults = useCallback(
        (query: string): Promise<ExtendedEmailOverviewDto[]> => {
            const key = `${emailReplicationFolderId}|${props.file.reference}|${query}`
            if (searchCacheRef.current?.key === key) {
                return searchCacheRef.current.promise
            }
            const promise = axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select:
                            'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                        $top: SEARCH_MAX_RESULTS,
                        //The folder already scopes to the file; the reference guards against Graph ignoring folder scope with $search.
                        $search: `"${query} ${props.file.reference}"`,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): ExtendedEmailOverviewDto[] => {
                    const value = response.data.value || []
                    setSearchTruncated(value.length >= SEARCH_MAX_RESULTS)
                    return value.map(mapMessage)
                })
                .catch(error => {
                    searchCacheRef.current = undefined
                    throw error
                })
            searchCacheRef.current = { key, promise }
            return promise
        },
        [props.file, emailReplicationFolderId, mapMessage],
    )

    const countItems = useCallback(
        (_column?: number, filterValues?: EmailFilterType): Promise<BaseOverviewCount> => {
            if (filterValues.query) {
                return fetchSearchResults(filterValues.query).then(results => ({
                    total: applySearchFilters(results, filterValues).length,
                }))
            }
            setSearchTruncated(false)
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select: 'id',
                        $filter: [
                            `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                            filterValues.from && `sentDateTime ge ${filterValues.from}`,
                            filterValues.to && `sentDateTime le ${filterValues.to}`,
                        ]
                            .filter(Boolean)
                            .join(' and '),
                        $count: true,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): BaseOverviewCount => ({
                    total: response.data['@odata.count'] ?? response.data.value.length ?? 0,
                }))
        },
        [props.file, emailReplicationFolderId, fetchSearchResults],
    )

    const loadItems = useCallback(
        (
            _column?: number,
            filterValues?: EmailFilterType,
            sortingValues?: EmailSortingType,
            start?: number,
            size?: number,
        ): Promise<ExtendedEmailOverviewDto[]> => {
            if (filterValues.query) {
                return fetchSearchResults(filterValues.query).then(results =>
                    applySearchFilters(results, filterValues, sortingValues).slice(start, start + size),
                )
            }
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select:
                            'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                        $filter: [
                            `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                            filterValues.from && `sentDateTime ge ${filterValues.from}`,
                            filterValues.to && `sentDateTime le ${filterValues.to}`,
                        ]
                            .filter(Boolean)
                            .join(' and '),
                        $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
                        $top: `${size}`,
                        $skip: `${start}`,
                        $orderby: `${sortingValues.property[0]} ${sortingValues.direction.toLowerCase()}`,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): ExtendedEmailOverviewDto[] =>
                    (response.data.value || []).map(mapMessage),
                )
        },
        [props.file, emailReplicationFolderId, fetchSearchResults, mapMessage],
    )

    if (emailReplicationFolderId === undefined) {
        return <Loading />
    }
    if (emailReplicationFolderId === null) {
        return (
            <MessageBar intent={'error'}>
                <MessageBarBody>{translate('file.pivot.email.error.no_folder')}</MessageBarBody>
            </MessageBar>
        )
    }

    return (
        <>
            <HammockDialog<PreviewMessageDialogBodyProps>
                open={openPreviewMessage !== undefined}
                setOpen={(open: boolean) => setOpenPreviewMessage(open ? openPreviewMessage : undefined)}
                dialogBody={PreviewMessageDialogBody}
                surfaceProps={{
                    className: dialogStyles.dialogHeight,
                }}
                dialogBodyProps={() => ({
                    index: items.indexOf(openPreviewMessage),
                    message: openPreviewMessage,
                    destination: Destination.ReplicationEmail,
                    hasPrevious: items.indexOf(openPreviewMessage) > 0,
                    hasNext: count.total > items.indexOf(openPreviewMessage) + 1,
                    previous: () => setOpenPreviewMessage(items[items.indexOf(openPreviewMessage) - 1]),
                    next: () => {
                        ;(items.length > items.indexOf(openPreviewMessage) + 1
                            ? Promise.resolve(items)
                            : overviewApiRef.current?.onUpdatePageItems?.(0, undefined, items.length, undefined)
                        ).then(_items => {
                            setOpenPreviewMessage(_items[_items.indexOf(openPreviewMessage) + 1])
                        })
                    },
                })}
            />

            {searchTruncated && (
                <MessageBar intent={'warning'}>
                    <MessageBarBody>{translate('file.pivot.email.search.truncated', { count: SEARCH_MAX_RESULTS })}</MessageBarBody>
                </MessageBar>
            )}

            <BaseOverview<
                ExtendedEmailOverviewDto,
                EmailFilterType,
                EmailSortingType,
                BaseOverviewCount,
                EmailHeaderActionsOverviewProps,
                EmailHeaderFilterTagsOverviewProps
            >
                defaultFilterValues={defaultFilterValues}
                countItems={countItems}
                loadItems={loadItems}
                onCountItems={(_column, result) => setCount(result)}
                onLoadItems={(_column, _columnPages, items) => setItems([...items])}
                listItemHeaderContent={BaseHeaderItem}
                listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
                listItemContent={(props: EmailLineOverviewProps) => (
                    <FileEmailListItem {...props} onPreviewClick={item => setOpenPreviewMessage(item)} />
                )}
                listItemClassnames={props => [
                    itemStyles.listEmailItem,
                    props.item && selectedItems.includes(props.item) && itemStyles.listEmailItemSelected,
                ]}
                listColumnFullWidth
                headerActionsButtons={FileEmailHeaderActionsButtons}
                headerActionsButtonsProps={{
                    emailUserFolderId: emailUserFolderId,
                    setEmailUserFolderId: setEmailUserFolderId,
                    reload: () => {
                        setSelectedItems([])
                        reload()
                    },
                }}
                headerDefaultFilterTags={FileEmailHeaderFilterTags}
                headerDefaultFilterTagsProps={{
                    selectedItems: selectedItems,
                    clearSelection: () => setSelectedItems([]),
                    reload: () => {
                        setSelectedItems([])
                        reload()
                    },
                }}
                headerSortingItems={headerSortingItems}
                filterContent={EmailFilter}
                filterInputHint={FilterInputHint}
                filterFromJson={values => ({
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    // to: values.tag ? generateToDate(values.tag) : values.to,
                })}
                filterToJson={values => ({
                    ...values,
                    from: values.tag ? '' : values.from,
                    // to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                })}
                inputPlaceholderKey={'email.search.placeholder'}
                localStorageKeyPrefix={'HammockFileEmailOverview'}
                apiRef={overviewApiRef}
                reloadTrigger={reloadTrigger}
                isItemSelectable={() => true}
                isItemSelected={item => selectedItems.includes(item)}
                onItemSelected={(data, item) => {
                    if (data.checked === true) {
                        setSelectedItems(prevItems => [...new Set([...prevItems, item])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== item.id)])])
                    }
                }}
                onItemClicked={props => {
                    if (selectedItems.includes(props.item)) {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== props.item.id)])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems, props.item])])
                    }
                }}
                {...props}
            />
        </>
    )
}
